import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { resources, DEFAULT_LOCALE, LOCALES } from '@insolech/i18n';

/**
 * Phase 7 — i18n init for the web app.
 *
 * Behaviour Rafael wants (Phase 7 §C / item C):
 *  - Default = English. The app NEVER auto-translates to es/ar based on the
 *    browser locale. (Previously the LanguageDetector picked navigator
 *    locale, which surprised users on Spanish-locale Chrome.)
 *  - The ONLY input that overrides the English default is an explicit user
 *    choice: either an entry in `localStorage[insolech-locale]` or, when
 *    signed in, a `profiles.language_preference` row (handled by the
 *    LanguageSwitcher component, which writes both).
 *
 * The detector is kept (rather than removed) because i18next-browser-language
 * detector handles the localStorage read/write plumbing for us. The trick:
 *  - `lng: DEFAULT_LOCALE` is set explicitly, which overrides the detector's
 *    initial pick when there's nothing in localStorage. With `lng` AND
 *    `detection.order: ['localStorage']`, the detector still re-reads
 *    localStorage on init — so a user who explicitly chose Spanish stays on
 *    Spanish, but a fresh visitor with `navigator.language === 'es'` lands on
 *    English.
 *  - `caches: ['localStorage']` makes `i18next.changeLanguage(...)` persist
 *    automatically, which keeps the LanguageSwitcher implementation simple.
 */
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    // Explicit default — without this, the detector's pick wins even when
    // localStorage is empty. With this, English is the floor and only an
    // explicit localStorage entry can lift it.
    lng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    // Critical: Arabic + Spanish namespaces are stubs (empty strings) until
    // Phase 4f AI-translates them. Without this, browsers with es/ar locale
    // render empty strings — button text disappears, headings vanish, etc.
    // With this, empty translations fall back to fallbackLng (English).
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    detection: {
      // Phase 7 — ONLY localStorage. Removed `cookie` and `navigator` so the
      // app never auto-detects from the browser locale (Rafael's feedback:
      // "Web app auto-translates to Spanish when browser locale is es" —
      // user wants English as the default + a dropdown in the top nav).
      order: ['localStorage'],
      lookupLocalStorage: 'insolech-locale',
      caches: ['localStorage'],
    },
  });

export default i18n;
